import { Octokit } from '@octokit/rest';
import {
  ConfigApi,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type { GithubActionsApi } from '@backstage-community/plugin-github-actions';

/**
 * GitHub Actions client that calls api.github.com through the Backstage proxy
 * (`/api/proxy/github/api`) using the org GITHUB_TOKEN, so workflow runs load
 * without requiring a second user OAuth popup for the `repo` scope.
 */
export class ProxyGithubActionsClient implements GithubActionsApi {
  constructor(
    private readonly options: {
      configApi: ConfigApi;
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  private async getOctokit(_hostname = 'github.com'): Promise<Octokit> {
    const proxyBase = await this.options.discoveryApi.getBaseUrl('proxy');
    // Proxy injects Authorization from app-config; forward Backstage session cookies/token.
    const fetchImpl: typeof fetch = (input, init) =>
      this.options.fetchApi.fetch(String(input), init);

    return new Octokit({
      baseUrl: `${proxyBase}/github/api`,
      request: { fetch: fetchImpl },
    });
  }

  async reRunWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }): Promise<any> {
    const { hostname, owner, repo, runId } = options;
    const octokit = await this.getOctokit(hostname);
    return octokit.actions.reRunWorkflow({
      owner,
      repo,
      run_id: runId,
    });
  }

  async listWorkflowRuns(options: {
    hostname?: string;
    owner: string;
    repo: string;
    pageSize?: number;
    page?: number;
    branch?: string;
  }): Promise<any> {
    const { hostname, owner, repo, pageSize = 100, page = 0, branch } = options;
    const octokit = await this.getOctokit(hostname);
    const workflowRuns = await octokit.actions.listWorkflowRunsForRepo({
      owner,
      repo,
      per_page: pageSize,
      page,
      ...(branch ? { branch } : {}),
    });
    return workflowRuns.data;
  }

  async getWorkflow(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }): Promise<any> {
    const { hostname, owner, repo, id } = options;
    const octokit = await this.getOctokit(hostname);
    const workflow = await octokit.actions.getWorkflow({
      owner,
      repo,
      workflow_id: id,
    });
    return workflow.data;
  }

  async getWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
  }): Promise<any> {
    const { hostname, owner, repo, id } = options;
    const octokit = await this.getOctokit(hostname);
    const run = await octokit.actions.getWorkflowRun({
      owner,
      repo,
      run_id: id,
    });
    return run.data;
  }

  async listJobsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    id: number;
    pageSize?: number;
    page?: number;
  }): Promise<any> {
    const { hostname, owner, repo, id, pageSize = 100, page = 0 } = options;
    const octokit = await this.getOctokit(hostname);
    const jobs = await octokit.actions.listJobsForWorkflowRun({
      owner,
      repo,
      run_id: id,
      per_page: pageSize,
      page,
    });
    return jobs.data;
  }

  async downloadJobLogsForWorkflowRun(options: {
    hostname?: string;
    owner: string;
    repo: string;
    runId: number;
  }): Promise<any> {
    const { hostname, owner, repo, runId } = options;
    const octokit = await this.getOctokit(hostname);
    const workflow = await octokit.actions.downloadJobLogsForWorkflowRun({
      owner,
      repo,
      job_id: runId,
    });
    return workflow.data;
  }

  async listBranches(options: {
    hostname?: string;
    owner: string;
    repo: string;
    page: number;
  }): Promise<any> {
    const { hostname, owner, repo, page } = options;
    const octokit = await this.getOctokit(hostname);
    const response = await octokit.rest.repos.listBranches({
      owner,
      repo,
      per_page: 100,
      page,
    });
    return response.data;
  }

  async getDefaultBranch(options: {
    hostname?: string;
    owner: string;
    repo: string;
  }): Promise<string> {
    const { hostname, owner, repo } = options;
    const octokit = await this.getOctokit(hostname);
    const response = await octokit.rest.repos.get({
      owner,
      repo,
    });
    return response.data.default_branch;
  }
}
